// packages/studio/web/src/proposals/changeDoc.ts
//
// Pure helpers for a proposal's optional change document (Phase B). A change
// document is a real `.md` file in the workspace VFS at
//   <target-dir>/docs/decisions/<YYYY-MM-DD>-<slug>.md
// authored as an editor_draft and materialised to git on propose. These
// helpers compute its path, frontmatter, and the proposal's default target;
// they are side-effect-free so they unit-test without a browser or a store.

import type { Container, ContainerDiscovery } from "./proposalsApi.js";

/** Folder leaf under a target where change documents live (ADR convention). */
export const DECISIONS_DIR = "docs/decisions";

export type TargetKind = "pkg" | "space";

export interface ParsedTarget {
  readonly kind: TargetKind;
  /** Bare tree path of the container ("" = repo root / root package). */
  readonly path: string;
}

/** Encode a picker selection into the `target` column string. */
export function encodeTarget(kind: TargetKind, path: string): string {
  return `${kind}:${path}`;
}

/**
 * Decode a stored `target`. Back-compat: the legacy default "" (and a bare
 * path with no `kind:` prefix) is the root package.
 */
export function parseTarget(target: string): ParsedTarget {
  if (target === "") return { kind: "pkg", path: "" };
  const i = target.indexOf(":");
  if (i === -1) return { kind: "pkg", path: target };
  const kind = target.slice(0, i);
  return { kind: kind === "space" ? "space" : "pkg", path: target.slice(i + 1) };
}

/** Strip a leading slash so tree paths compare uniformly. */
function norm(path: string): string {
  return path.replace(/^\/+/, "");
}

/**
 * The primary package for a changeset — the package owning the most touched
 * `.arch` files (ties broken by the deepest/longest package path, mirroring
 * `ownerOf`'s longest-prefix rule). Falls back to the root package (path "" /
 * implicit), then the sole package, then "" (root). Returns an encoded target.
 */
export function primaryPackageTarget(
  touched: readonly string[],
  packages: readonly Container[],
): string {
  const arch = touched.map(norm).filter((p) => p.endsWith(".arch"));

  const owns = (pkg: Container, file: string): boolean => {
    const base = norm(pkg.path);
    if (base === "") return true; // root package owns everything
    return file === base || file.startsWith(base + "/");
  };

  if (arch.length > 0) {
    // Each file belongs to its DEEPEST owning package (longest-prefix, like
    // `ownerOf`); count per owning package so the root package only wins files
    // no nested package claims.
    const counts = new Map<string, { pkg: Container; n: number }>();
    for (const f of arch) {
      let owner: Container | null = null;
      for (const pkg of packages) {
        if (!owns(pkg, f)) continue;
        if (!owner || norm(pkg.path).length > norm(owner.path).length) owner = pkg;
      }
      if (!owner) continue;
      const key = owner.path;
      const cur = counts.get(key) ?? { pkg: owner, n: 0 };
      cur.n++;
      counts.set(key, cur);
    }
    let best: Container | null = null;
    let bestScore = -1;
    for (const { pkg, n } of counts.values()) {
      // more files wins; tie → longer (deeper) package path wins.
      const score = n * 10000 + norm(pkg.path).length;
      if (score > bestScore) {
        bestScore = score;
        best = pkg;
      }
    }
    if (best) return encodeTarget("pkg", best.path);
  }

  // No touched .arch (doc-only draft) → the root package.
  const root =
    packages.find((p) => norm(p.path) === "") ??
    packages.find((p) => p.implicit) ??
    (packages.length === 1 ? packages[0] : null);
  return encodeTarget("pkg", root?.path ?? "");
}

/**
 * Resolve a parsed target to its directory in the tree. A package target → its
 * manifest directory (repo root "" for the root package); a space target → its
 * discovered `spaces:{path}` (a `name:`-space is transparent + manifest-less,
 * so the discovered path is authoritative, not a manifest dir).
 */
export function resolveTargetDir(target: ParsedTarget, discovery: ContainerDiscovery): string {
  const list = target.kind === "space" ? discovery.spaces : discovery.packages;
  const hit = list.find((c) => c.path === target.path);
  return norm(hit?.path ?? target.path);
}

/** kebab-case, punctuation-stripped, clamped slug of a title. */
export function slugifyTitle(title: string, maxLen = 50): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return (slug || "untitled").slice(0, maxLen).replace(/-+$/g, "");
}

/** `<target-dir>/docs/decisions/<date>-<slug>.md`, fixed at doc creation. */
export function decisionDocPath(dir: string, date: string, slug: string): string {
  const leaf = `${DECISIONS_DIR}/${date}-${slug}.md`;
  return dir ? `${norm(dir)}/${leaf}` : leaf;
}

/**
 * The canonical `space:` frontmatter pin for a target: a space target → its
 * dotted name (falling back to its path segments); a package target → the root
 * sentinel (empty), so `parseMarkdownDocument` resolves the doc to the root
 * space rather than the bogus `docs/decisions/` path space.
 */
export function frontmatterSpace(target: ParsedTarget, discovery: ContainerDiscovery): string {
  if (target.kind !== "space") return "";
  const hit = discovery.spaces.find((c) => c.path === target.path);
  if (hit?.name) return hit.name;
  return norm(target.path).split("/").filter(Boolean).join(".");
}

export interface FrontmatterInput {
  readonly changeId: string;
  readonly title: string;
  readonly date: string;
  /** Canonical space name, or "" for the root space. */
  readonly space: string;
  /** Optional Studio cross-ref (proposal number / url). */
  readonly proposal?: string;
}

/** The change document's frontmatter + boilerplate body. */
export function composeChangeDoc(input: FrontmatterInput): string {
  const lines = [
    "---",
    `change-id: ${input.changeId}`,
    `title: ${input.title}`,
    "status: proposed",
    `date: ${input.date}`,
    `space: ${input.space}`,
  ];
  if (input.proposal) lines.push(`proposal: ${input.proposal}`);
  lines.push("---", "");
  const body = [
    `# ${input.title}`,
    "",
    "## Context",
    "",
    "What forces this change? What is the situation today?",
    "",
    "## Decision",
    "",
    "What are we changing, and why this option over the alternatives?",
    "",
    "## Consequences",
    "",
    "What becomes easier or harder as a result?",
    "",
  ];
  return lines.join("\n") + body.join("\n");
}
